<template>
  <div class="hotel-detailed-view min-h-screen bg-background/50 dark:bg-background/80">
    <BubbleHeader :bubble="bubble" @update:bubble="handleBubbleUpdate" />

    <div class="max-w-7xl mx-auto p-4 sm:p-6 space-y-8">
      <Card class="mb-6 overflow-hidden">
        <CardContent class="p-0">
          <div class="relative aspect-[21/9] w-full">
            <NuxtImg
              :src="getCoverPhoto ?? undefined"
              :alt="bubble.title"
              class="absolute inset-0 w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              loading="eager"
              placeholder
              sizes="(max-width: 768px) 100vw, 1280px"
              format="webp"
              quality="90"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            <div class="absolute bottom-0 left-0 right-0 p-6">
              <h1 class="text-3xl font-bold text-white drop-shadow-lg">{{ bubble.title }}</h1>
            </div>
          </div>
        </CardContent>
      </Card>

      <TypeChips :primary-type="bubble.primaryType" :types="bubble.types" />

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Details
          :address="bubble.address"
          :phone-number="bubble.phoneNumber ?? undefined"
          :website="bubble.website ?? undefined"
          :price-range="bubble.priceRange ?? undefined"
          :rating="bubble.rating"
          :rating-count="bubble.ratingCount"
          :star-rating="bubble.starRating"
        />

        <Hours :hours="bubble.hoursOfOperation" />
      </div>

      <Description v-if="bubble.description" :description="bubble.description" />

      <Gallery :photos="getPhotos" :title="bubble.title" />

      <div v-if="bubble.amenities" class="mb-6">
        <h3 class="text-xl font-semibold mb-2">Amenities</h3>
        <ul class="list-disc list-inside">
          <li v-for="amenity in bubble.amenities" :key="amenity" class="mb-1">
            {{ amenity }}
          </li>
        </ul>
      </div>

      <!-- Stats Section -->
      <div
        class="bg-card dark:bg-card/50 rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl"
      >
        <div class="p-6 border-b border-border dark:border-border/50">
          <h2 class="text-xl font-semibold flex items-center gap-2">
            <Icon name="ph:chart-bar" class="w-5 h-5 text-primary" />
            Your Stats
          </h2>
        </div>
        <BubbleStats
          :bubble-uuid="bubble.uuid"
          did-label="Stayed"
          placeholder="Write your thoughts about this hotel..."
        />
      </div>

      <ActionButtons
        :website="bubble.website ?? undefined"
        :location="bubble.location"
        :booking-url="bubble.bookingUrl"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { HotelBubble, Bubble, AnyBubble } from '@/stores/interfaces'
import { useMediaResources } from '@/composables/useMediaResources'
import BubbleHeader from '../BubbleHeader.vue'
import { Card, CardContent } from '@/components/ui/card'
import Details from '../specific/places/Details.vue'
import Description from '../specific/places/Description.vue'
import Gallery from '../specific/places/Gallery.vue'
import TypeChips from '../specific/places/TypeChips.vue'
import ActionButtons from '../specific/places/ActionButtons.vue'
import Hours from '../specific/places/Hours.vue'
import BubbleStats from '../BubbleStats.vue'
import { useTypeNavigation } from '@/composables/useTypeNavigation'

const props = defineProps<{
  bubble: HotelBubble
}>()

const localBubble = ref(props.bubble)
const { getCoverPhoto, getPhotos } = useMediaResources(props.bubble)

// Use the type navigation composable
useTypeNavigation(props.bubble)

const handleBubbleUpdate = (updatedBubble: Bubble | AnyBubble) => {
  // Type guard to ensure we're working with a HotelBubble
  if (
    'bookingUrl' in updatedBubble &&
    'starRating' in updatedBubble &&
    'amenities' in updatedBubble
  ) {
    localBubble.value = updatedBubble as HotelBubble
  }
}
</script>
